import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { customerApi, scheduleApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { usePlanFeature } from '../components/plan-gate.tsx';
import { UpgradeNudge } from '../components/upgrade-nudge.tsx';
import { Alert, Button, EmptyState, LoadingBlock, PageHeader, Pagination, StatusBadge } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import CustomerForm from '../forms/customer-form.tsx';
import PropertyForm from '../forms/property-form.tsx';
import BillingCard from '../components/payments/billing-card.tsx';
import { CustomerOwedLine, CustomerQuotePaymentHistory } from '../components/payments/customer-quote-payments.tsx';
import { PortalLinkSection } from '../components/portal-link-section.tsx';
import {
  CustomerCommunication,
  customerConversationQuery,
  isCustomerHistory,
} from '../components/customer-communication.tsx';
import { AppointmentBadge } from '../components/schedule/appointment-badge.tsx';
import AddressLink from '../components/address-link.tsx';
import CustomerCallPanel from '../components/customer-call-panel.tsx';
import { describeRule } from '../components/schedule/format.ts';
import { quoteAmount } from '../components/quote/plain-price.tsx';
import { formatLocalTime, formatNumericDate, formatShortDate } from '../i18n/datetime.ts';
import { formatAddressLine } from '@shared/maps.ts';
import type { SeriesFrequency } from '@shared/constants/index.ts';
import { getDateLocale } from '../i18n/locale.ts';
import type { Property } from '@shared/types/index.ts';

/**
 * A screenful. This list used to arrive whole (capped at 200 by the query, with
 * nothing said about the cap), which for a customer of several years meant a
 * page that got slower every quarter for a panel nobody scrolls past the top of.
 */
const QUOTES_PAGE_SIZE = 20;

/**
 * Recurring schedules per page in the schedule panel.
 *
 * Small because a customer with many is the exception, but the panel still has
 * to be able to reach them: this list is paged server-side now, so without a
 * pager everything past the first page would be invisible rather than merely
 * further down.
 */
const SERIES_PAGE_SIZE = 10;

export default function CustomerDetailPage() {
  const { t } = useTranslation();
  const { customerId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const history = isCustomerHistory(location.search);
  const overviewSearch = new URLSearchParams(location.search);
  overviewSearch.delete('history');
  const sectionLink = (hash: string) => ({ pathname: location.pathname, search: overviewSearch.toString(), hash });
  const queryClient = useQueryClient();
  const { can, company } = useSession();
  const hasScheduling = usePlanFeature('scheduling');
  // Calling from the company's number: where the installation answers calls,
  // the plan includes them, and the person may write to customers (texting
  // them from the inbox asks the same). Otherwise the phone's own dialer.
  const hasVoice = usePlanFeature('voice_inbox');
  const canCallFromCompany = Boolean(company?.voiceInboxAvailable) && hasVoice && can('customers:write');
  const [calling, setCalling] = useState(false);
  const hasMessaging = usePlanFeature('messaging');
  const hasInvoicing = usePlanFeature('invoicing');
  // Start the communication lookup with the customer request, not after it.
  useQuery({ ...customerConversationQuery(customerId), enabled: hasMessaging && Boolean(customerId) });

  const [quotePage, setQuotePage] = useState(1);
  const [seriesPage, setSeriesPage] = useState(1);
  const [editing, setEditing] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const customer = useQuery({ queryKey: ['customer', customerId], queryFn: () => customerApi.get(customerId) });
  const properties = useQuery({
    queryKey: ['customer-properties', customerId],
    queryFn: () => customerApi.properties(customerId),
  });
  const quotes = useQuery({
    queryKey: ['customer-quotes', customerId, quotePage],
    queryFn: () => customerApi.quotes(customerId, { page: quotePage, pageSize: QUOTES_PAGE_SIZE }),
    // Keeps the previous page on screen while the next one loads, so the pager
    // neither disappears nor forgets how many pages there are mid-click — but
    // only within one customer. This component stays mounted across an id
    // change, and the customer record is a cheaper read than these lists, so
    // holding the old data would put one customer's name above another
    // customer's quote and schedule links. They are links; following one would
    // take you to the wrong customer's record.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === customerId ? previous : undefined),
  });
  const schedule = useQuery({
    queryKey: ['customer-schedule', customerId, seriesPage],
    queryFn: () => scheduleApi.customerSchedule(customerId, { page: seriesPage, pageSize: SERIES_PAGE_SIZE }),
    // Keeps the previous page on screen while the next one loads, so the pager
    // neither disappears nor forgets how many pages there are mid-click — but
    // only within one customer. This component stays mounted across an id
    // change, and the customer record is a cheaper read than this panel, so
    // holding the old data would put one customer's name above another
    // customer's quote and schedule links. They are links; following one would
    // take you to the wrong customer's record.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === customerId ? previous : undefined),
    enabled: can('schedule:read') && hasScheduling,
  });

  // Whether the page has already scrolled to the current URL fragment (see
  // below). Declared up here so the customerId-reset effect can clear it too.
  const scrolledHashRef = useRef<string | null>(null);

  // Opening a different customer starts at the beginning of their lists. React
  // Router keeps this component mounted when only the id changes, so without
  // this the page number carries over — and page 3 of a customer with one page
  // of quotes is an empty panel nobody asked for.
  //
  // An open form resets for a much sharper reason: it is the previous
  // customer's form, holding the previous customer's typed values, and its
  // save writes to whichever customer the page is now showing. The record
  // itself is usually already in the React Query cache (Back, or the command
  // palette), so nothing unmounts on its own to close it. The forms below are
  // keyed by customer id as well — belt and braces, since a form can also
  // have its `customer` prop swapped underneath it.
  //
  // The delete error goes with them: it was raised about the customer that
  // has just left the screen.
  //
  // The "already scrolled to this hash" marker resets here too: two
  // failed-payment links in a row can carry the identical fragment
  // (#billing-heading) to two different customers, and the hash alone can't
  // tell those apart.
  useEffect(() => {
    setQuotePage(1);
    setSeriesPage(1);
    setEditing(false);
    setEditingProperty(null);
    setError(null);
    // A call panel belongs to one customer: the next one starts closed.
    setCalling(false);
    scrolledHashRef.current = null;
  }, [customerId]);

  // A visit's "update card on file" link lands here with a fragment (e.g.
  // #billing-heading). React Router does not scroll to it on its own — that
  // is a full-page-load browser behavior — so this runs it by hand once the
  // section it names has actually rendered. `customer.data` stays a dependency
  // because the target section (e.g. BillingCard) does not exist in the DOM
  // until the customer has loaded, so the first render or two find nothing to
  // scroll to; the ref above stops it from firing again on every later
  // refetch (editing the customer's name, say) once it has already landed —
  // the hash in the URL is otherwise indistinguishable from a fresh arrival.
  useEffect(() => {
    if (!location.hash) {
      scrolledHashRef.current = null;
      return;
    }
    const marker = `${location.key}:${location.hash}`;
    if (scrolledHashRef.current === marker) return;
    const target = document.getElementById(location.hash.slice(1));
    if (!target) return;
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    scrolledHashRef.current = marker;
  }, [location.hash, location.key, customer.data, history]);

  useDocumentTitle(customer.data?.displayName ?? t('customers.detail.documentTitleFallback'));

  const remove = useMutation({
    mutationFn: () => customerApi.remove(customerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
      navigate('/customers', { replace: true });
    },
    onError: (caught) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('customers.detail.deleteError')),
  });

  if (customer.isLoading) return <LoadingBlock label={t('customers.detail.loading')} />;

  if (customer.isError || !customer.data) {
    return (
      <Alert tone="error" title={t('customers.detail.loadErrorTitle')}>
        <p>{(customer.error as Error | null)?.message ?? t('customers.detail.mayHaveBeenDeleted')}</p>
        <Button className="mt-2" busy={customer.isFetching} onClick={() => void customer.refetch()}>
          {t('common.retry')}
        </Button>
        <Link to="/customers" className="mt-2 inline-block text-[color:var(--color-brand)] hover:underline">
          {t('customers.detail.backToAll')}
        </Link>
      </Alert>
    );
  }

  // Each quote is shown in the currency it was written in, which is what the
  // snapshot on the row is for — reading the company's current setting would
  // restate quotes already sent. A quote accepted at a recurring frequency
  // owes the reader "/visit, weekly" in the same glance — its stored total
  // IS that cadence's price (same rule as the quotes list).
  // A quote sent as a price range reads as its band, not the band's middle.
  const quoteMoney = (quote: Parameters<typeof quoteAmount>[0] & { acceptedFrequency: SeriesFrequency | null }) => {
    const amount = quoteAmount(quote, t);
    if (quote.acceptedFrequency && quote.acceptedFrequency !== 'one_time') {
      return t('quotes.list.perVisitFrequency', {
        amount,
        frequency: t(`schedule.frequency.${quote.acceptedFrequency}`),
      });
    }
    return amount;
  };

  return (
    <div className="min-w-0 [overflow-wrap:anywhere] [&_.btn]:max-w-full [&_.btn]:whitespace-normal">
      <PageHeader
        title={customer.data.displayName}
        description={
          [
            customer.data.serviceSegment === 'commercial'
              ? t('customers.detail.serviceSegmentBusiness')
              : t('customers.detail.serviceSegmentHome'),
            customer.data.email,
            customer.data.phone,
          ]
            .filter(Boolean)
            .join(' · ') || undefined
        }
        actions={
          <>
            {can('customers:write') && !editing ? (
              <Button onClick={() => setEditing(true)}>{t('customers.detail.editDetails')}</Button>
            ) : null}
            {can('customers:delete') ? (
              <Button
                variant="danger"
                busy={remove.isPending}
                onClick={() => {
                  if (window.confirm(t('customers.detail.confirmDelete'))) remove.mutate();
                }}
              >
                {t('customers.detail.delete')}
              </Button>
            ) : null}
          </>
        }
      />

      <nav className="mb-6 flex flex-wrap gap-2" aria-label={t('customers.detail.sections')}>
        {customer.data.phone && canCallFromCompany ? (
          <>
            <Button onClick={() => setCalling(true)}>{t('customerCall.button')}</Button>
            <a className="btn btn-secondary" href={`tel:${customer.data.phone}`}>
              {t('customerCall.onThisDevice')}
            </a>
          </>
        ) : customer.data.phone ? (
          <a className="btn btn-secondary" href={`tel:${customer.data.phone}`}>
            {t('customers.detail.callCustomer')}
          </a>
        ) : null}
        {customer.data.email ? (
          <a className="btn btn-secondary" href={`mailto:${customer.data.email}`}>
            {t('customers.detail.emailCustomer')}
          </a>
        ) : null}
        {can('schedule:read') && hasScheduling ? (
          <Link className="btn btn-secondary" to={sectionLink('#schedule-heading')}>
            {t('customers.detail.nextVisits')}
          </Link>
        ) : null}
        <Link className="btn btn-secondary" to={sectionLink('#quotes-heading')}>
          {t('customers.detail.quotesHeading')}
        </Link>
        {can('payments:read') && hasInvoicing ? (
          <Link className="btn btn-secondary" to={sectionLink('#billing-heading')}>
            {t('customers.detail.billingSection')}
          </Link>
        ) : can('payments:read') ? (
          <Link className="btn btn-secondary" to={sectionLink('#customer-payments-heading')}>
            {t('customers.detail.paymentsSection')}
          </Link>
        ) : null}
        <Link className="btn btn-secondary" to={sectionLink('#properties-heading')}>
          {t('customers.detail.propertiesHeading')}
        </Link>
      </nav>

      {calling ? (
        <CustomerCallPanel
          key={customer.data.id}
          customerId={customer.data.id}
          customerName={customer.data.displayName}
          onClose={() => setCalling(false)}
        />
      ) : null}

      {customer.data.teamFlexible ? <p className="mb-3 text-sm">{t('savedTeams.flexible')}</p> : null}
      {/* The team's notes on this customer, on every plan, Free included:
          they are written to be read before the next job, not only when
          somebody opens the form to edit them. */}
      {!editing && customer.data.internalNotes ? (
        <p className="mb-6 whitespace-pre-line text-sm">
          <span className="font-medium">{t('customerForm.internalNotes')}:</span> {customer.data.internalNotes}
        </p>
      ) : null}

      {error ? (
        <div className="mb-6">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {editing ? (
        <section className="card mb-6" aria-label={t('customers.detail.editAriaLabel')}>
          <CustomerForm
            key={customer.data?.id ?? customerId}
            customer={customer.data}
            onSaved={() => {
              setEditing(false);
              void customer.refetch();
            }}
            onCancel={() => setEditing(false)}
          />
        </section>
      ) : null}

      <UpgradeNudge id="customer-texting" messageKey="texting" />
      {hasMessaging ? <CustomerCommunication key={customerId} customerId={customerId} /> : null}

      {/* Keep forms and payment actions mounted while reading history. */}
      <div hidden={history && hasMessaging}>
        <div className="grid min-w-0 items-start gap-6 xl:grid-cols-2 [&>section]:mb-0 [&>section]:min-w-0">
          {/* ---- Schedule ---------------------------------------------------- */}
          {/* Scheduling is a plan feature, so on Free this panel is absent rather
          than empty: the endpoint behind it answers 402, and an empty
          "Schedule" heading would read as "this customer has no visits". */}
          {can('schedule:read') && hasScheduling ? (
            <section className="card mb-6" aria-labelledby="schedule-heading">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 id="schedule-heading" className="text-lg font-semibold">
                  {t('customers.detail.scheduleHeading')}
                </h2>
                {can('schedule:write') ? (
                  <Link to={`/schedule/new?customerId=${customerId}`} className="btn btn-secondary">
                    {t('customers.detail.scheduleVisits')}
                  </Link>
                ) : null}
              </div>

              {schedule.isError ? (
                <div className="mb-3">
                  <Alert tone="error">{t('customers.detail.scheduleError')}</Alert>
                  <Button busy={schedule.isFetching} onClick={() => void schedule.refetch()}>
                    {t('common.retry')}
                  </Button>
                </div>
              ) : null}
              {schedule.data && schedule.data.series.length > 0 ? (
                <>
                  <ul className="mb-4 divide-y divide-[color:var(--color-line)]">
                    {schedule.data.series.map((series) => (
                      <li key={series.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                        <Link
                          to={`/schedule/series/${series.id}`}
                          className="font-medium text-[color:var(--color-brand)] hover:underline"
                        >
                          {describeRule(series)}
                        </Link>
                        <span className="text-sm text-[color:var(--color-muted)]">
                          {t(`schedule.seriesStatus.${series.status}`)}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {schedule.data.upcoming.length > 0 ? (
                    <>
                      <h3 className="mb-2 text-sm font-semibold text-[color:var(--color-muted)]">
                        {t('customers.detail.nextVisits')}
                      </h3>
                      <ul className="divide-y divide-[color:var(--color-line)]">
                        {schedule.data.upcoming.slice(0, 5).map((appointment) => (
                          <li key={appointment.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
                            <span>
                              {formatShortDate(appointment.scheduledDate)} ·{' '}
                              {formatLocalTime(appointment.startTimeLocal)}
                            </span>
                            <span className="flex flex-wrap items-center gap-3">
                              <AppointmentBadge status={appointment.status} />
                              {/* "Can you do the fridge next time?" usually
                                  arrives while somebody has the customer open. */}
                              {hasInvoicing && can('quotes:write') ? (
                                <Link
                                  to={`/schedule?appointment=${encodeURIComponent(appointment.id)}&extraWork=1`}
                                  className="text-sm font-semibold text-[color:var(--color-brand)] underline"
                                  aria-label={t('changeOrders.addToVisitOn', {
                                    date: formatShortDate(appointment.scheduledDate),
                                  })}
                                >
                                  {t('changeOrders.raise')}
                                </Link>
                              ) : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </>
              ) : schedule.data ? (
                <p className="text-[color:var(--color-muted)]">{t('customers.detail.noVisitsScheduled')}</p>
              ) : schedule.isLoading ? (
                <LoadingBlock label={t('customers.detail.loadingSchedule')} />
              ) : null}

              {/* Outside the branch above: an out-of-range page renders no rows, and
              a pager nested inside "has rows" would not be mounted to walk back
              off it — leaving the panel stuck on an empty state with no control
              to leave it. */}
              <Pagination
                page={seriesPage}
                totalPages={
                  schedule.data
                    ? Math.max(1, Math.ceil(schedule.data.seriesTotal / schedule.data.seriesPageSize))
                    : undefined
                }
                onPageChange={setSeriesPage}
              />
            </section>
          ) : null}

          {/* ---- Quotes ------------------------------------------------------ */}
          <section className="card" aria-labelledby="quotes-heading">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 id="quotes-heading" className="text-lg font-semibold">
                {t('customers.detail.quotesHeading')}
              </h2>
              {can('quotes:write') ? (
                <Link to={`/quotes/new?customerId=${encodeURIComponent(customerId)}`} className="btn btn-secondary">
                  {t('customers.detail.newQuote')}
                </Link>
              ) : null}
            </div>

            <CustomerOwedLine customer={customer.data} />

            {quotes.isError ? (
              <div className="mb-3">
                <Alert tone="error">{t('customers.detail.quotesError')}</Alert>
                <Button busy={quotes.isFetching} onClick={() => void quotes.refetch()}>
                  {t('common.retry')}
                </Button>
              </div>
            ) : null}
            {quotes.isLoading ? (
              <LoadingBlock label={t('customers.detail.loading')} />
            ) : quotes.data && quotes.data.items.length > 0 ? (
              <ul className="divide-y divide-[color:var(--color-line)]">
                {quotes.data.items.map((quote) => (
                  <li key={quote.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                    <div>
                      <Link
                        to={`/quotes/${quote.id}`}
                        className="font-semibold text-[color:var(--color-brand)] hover:underline"
                      >
                        {quote.quoteNumber}
                      </Link>
                      <span className="ml-3 text-sm text-[color:var(--color-muted)]">
                        {formatNumericDate(quote.quoteDate)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge status={quote.status} expired={quote.isExpired} quote={quote} />
                      <span className="tabular font-semibold">{quoteMoney(quote)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : !quotes.isError ? (
              <EmptyState title={t('customers.detail.emptyQuotesTitle')}>
                <p>{t('customers.detail.emptyQuotesMessage')}</p>
              </EmptyState>
            ) : null}

            <Pagination page={quotePage} totalPages={quotes.data?.totalPages} onPageChange={setQuotePage} />
          </section>
          {/* ---- Billing ----------------------------------------------------- */}
          {/* Invoicing, and everything it hangs off: a card on file, "Record
          payment", the portal link. All of it refuses on a plan without
          `invoicing`, so none of it is offered. */}
          {hasInvoicing ? (
            <BillingCard customer={customer.data} onChanged={() => void customer.refetch()} />
          ) : (
            // Without invoicing, what the plan does record: the payments taken
            // on this customer's quotes, with their receipts.
            <CustomerQuotePaymentHistory customer={customer.data} />
          )}

          <section className="card mb-6" aria-labelledby="properties-heading">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 id="properties-heading" className="text-lg font-semibold">
                {t('customers.detail.propertiesHeading')}
              </h2>
              {can('customers:write') && !editingProperty ? (
                <Button onClick={() => setEditingProperty('new')}>{t('customers.detail.addProperty')}</Button>
              ) : null}
            </div>

            {properties.isError ? (
              <div className="mb-3">
                <Alert tone="error">{t('customers.detail.propertiesError')}</Alert>
                <Button busy={properties.isFetching} onClick={() => void properties.refetch()}>
                  {t('common.retry')}
                </Button>
              </div>
            ) : null}
            {editingProperty ? (
              <PropertyForm
                key={customerId}
                customerId={customerId}
                {...(editingProperty !== 'new' ? { property: editingProperty } : {})}
                onSaved={() => {
                  setEditingProperty(null);
                  void properties.refetch();
                }}
                onCancel={() => setEditingProperty(null)}
              />
            ) : properties.data && properties.data.items.length > 0 ? (
              <ul className="divide-y divide-[color:var(--color-line)]">
                {properties.data.items.map((property) => (
                  <li key={property.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                    <div>
                      <p className="font-medium">
                        {property.name ?? property.addressLine1 ?? t('customers.detail.unnamedProperty')}
                      </p>
                      <p className="text-sm text-[color:var(--color-muted)]">
                        {/* The property carries its own coordinates where the address was
                        picked from a suggestion, so this is the one address in the app
                        whose link points at a surveyed point rather than a text search.

                        A property with no address text is not linked even where it does
                        have coordinates: the only thing left to show is "No address on
                        file", and making that sentence a link says the opposite of what
                        it says. */}
                        {formatAddressLine(property) ? (
                          <AddressLink address={property} />
                        ) : (
                          t('customers.detail.noAddressOnFile')
                        )}
                      </p>
                      <p className="text-sm text-[color:var(--color-muted)]">
                        {[
                          property.bedrooms !== null ? t('customers.detail.bed', { count: property.bedrooms }) : null,
                          property.bathrooms !== null
                            ? t('customers.detail.bath', { count: property.bathrooms })
                            : null,
                          property.squareFeet !== null
                            ? t('customers.detail.sqft', {
                                formattedCount: new Intl.NumberFormat(getDateLocale()).format(property.squareFeet),
                              })
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                      {/* How to get in — where a gate code or lockbox note lives —
                      and where to park: on every plan, Free included, because
                      the person driving there needs them whatever the plan. */}
                      {property.accessNotes ? (
                        <p className="mt-1 whitespace-pre-line text-sm">
                          <span className="font-medium">{t('workOrder.accessNotes')}:</span> {property.accessNotes}
                        </p>
                      ) : null}
                      {property.parkingNotes ? (
                        <p className="whitespace-pre-line text-sm">
                          <span className="font-medium">{t('workOrder.parkingNotes')}:</span> {property.parkingNotes}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex gap-2">
                      {can('schedule:read') && hasScheduling ? (
                        <Link
                          to={`/customers/${customerId}/properties/${property.id}/checklist`}
                          className="btn btn-secondary"
                        >
                          {t('checklists.open')}
                        </Link>
                      ) : null}
                      {can('customers:write') ? (
                        <Button onClick={() => setEditingProperty(property)}>{t('customers.detail.edit')}</Button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            ) : properties.isLoading ? (
              <LoadingBlock label={t('customers.detail.loading')} />
            ) : !properties.isError ? (
              <p className="text-[color:var(--color-muted)]">{t('customers.detail.noPropertiesYet')}</p>
            ) : null}
          </section>
          {hasInvoicing ? (
            <div className="xl:col-span-2">
              <details className="rounded border border-[color:var(--color-line)]">
                <summary className="cursor-pointer p-4 font-semibold">{t('customers.detail.portalSection')}</summary>
                <PortalLinkSection customer={customer.data} onChanged={() => void customer.refetch()} />
              </details>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
